import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Camera, Trash2, ShieldCheck, Shield, Download, Share, CheckCircle2 } from 'lucide-react';
import { useToast } from '../Toast';
import { useTheme } from '../Theme';
import { usePWA } from '../pwa';
import { pushSupported, getPushState, enablePush, disablePush, sendPushTest } from '../push';
import { toolNavTarget } from './Dashboard';

const API = process.env.REACT_APP_API_URL || '';
const BRAND = '#F05A28';

// Avatar via authenticated fetch → blob → objectURL (same pattern as PhotoImg).
function Avatar({ userId, bust, size = 96, dark }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false, obj;
    setUrl(null);
    fetch(`${API}/api/users/${userId}/photo`, { credentials: 'include' })
      .then(r => (r.ok ? r.blob() : Promise.reject()))
      .then(b => { if (!revoked) { obj = URL.createObjectURL(b); setUrl(obj); } })
      .catch(() => {});
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [userId, bust]);

  const ring = dark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500';
  return (
    <div
      className={`rounded-full overflow-hidden flex items-center justify-center ${ring}`}
      style={{ width: size, height: size }}
    >
      {url
        ? <img src={url} alt="Profile" className="w-full h-full object-cover" />
        : <Camera size={size * 0.4} />}
    </div>
  );
}

// Defined at module scope (NOT inside Account) so its identity is stable across renders —
// otherwise every keystroke would remount the inputs and drop focus after one character.
function Section({ T, title, desc, children }) {
  return (
    <div className={`rounded-xl border p-5 sm:p-6 ${T.card}`}>
      <h3 className={`font-semibold text-lg ${T.text}`}>{title}</h3>
      {desc && <p className={`text-sm mt-0.5 mb-4 ${T.sub}`}>{desc}</p>}
      {!desc && <div className="mb-4" />}
      {children}
    </div>
  );
}

export default function Account({ user, onBack, onUserUpdate, onNavigate }) {
  const toast = useToast();
  const { theme, setTheme } = useTheme();
  const pwa = usePWA();
  const dark = theme !== 'light';

  const installApp = async () => {
    const res = await pwa.promptInstall();
    if (res?.outcome === 'accepted') toast.success('Installing OlogyHQ…');
  };

  // ── push notifications ──
  const [pushState, setPushState] = useState({ supported: pushSupported(), subscribed: false, permission: 'default' });
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => { getPushState().then(setPushState).catch(() => {}); }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState.subscribed) { await disablePush(); toast.success('Notifications turned off for this device.'); }
      else { await enablePush(); toast.success('Notifications are on for this device.'); }
      setPushState(await getPushState());
    } catch (e) { toast.error(e.message); } finally { setPushBusy(false); }
  };

  const testPush = async (delay) => {
    if (delay) {
      toast.info(`Background or lock the app now — the test arrives in ~${delay}s.`);
      sendPushTest(delay).catch(() => {}); // server sends after the delay regardless of the app's state
      return;
    }
    try {
      const r = await sendPushTest(0);
      if (r.disabled) toast.error('Push is not configured on the server.');
      else if (r.sent > 0) toast.success('Test sent — check your notifications.');
      else toast.info('No devices are subscribed yet.');
    } catch (e) { toast.error(e.message); }
  };

  // theme-aware class tokens
  const T = {
    page:    dark ? 'bg-gray-900' : 'bg-gray-100',
    card:    dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    title:   dark ? 'text-cream' : 'text-gray-900',
    text:    dark ? 'text-white' : 'text-gray-900',
    sub:     dark ? 'text-gray-400' : 'text-gray-600',
    input:   dark ? 'bg-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 text-gray-900 border border-gray-300 placeholder-gray-400',
    chip:    dark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700',
    divider: dark ? 'border-gray-700' : 'border-gray-200',
  };

  const [me, setMe] = useState(user);
  const [name, setName] = useState(user.name || '');
  const [pronouns, setPronouns] = useState(user.pronouns || '');
  const [photoBust, setPhotoBust] = useState(0);
  const [tools, setTools] = useState([]);
  const fileRef = useRef(null);

  // load fresh profile + tool access
  useEffect(() => {
    fetch(`${API}/api/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setMe(d); setName(d.name || ''); setPronouns(d.pronouns || ''); })
      .catch(() => {});
    fetch(`${API}/api/my-tools`, { credentials: 'include' })
      .then(r => r.json()).then(setTools).catch(() => {});
  }, []);

  const pushUser = (patch) => { setMe(m => ({ ...m, ...patch })); onUserUpdate && onUserUpdate(u => ({ ...u, ...patch })); };

  // ── profile fields ──
  const saveProfile = async () => {
    try {
      const res = await fetch(`${API}/api/me`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, pronouns }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not save.');
      const d = await res.json();
      pushUser({ name: d.name, pronouns: d.pronouns });
      toast.success('Profile updated.');
    } catch (e) { toast.error(e.message); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await fetch(`${API}/api/me/photo`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Upload failed.');
      const d = await res.json();
      pushUser({ photo_filename: d.photo_filename });
      setPhotoBust(b => b + 1);
      toast.success('Photo updated.');
    } catch (e) { toast.error(e.message); }
  };

  const removePhoto = async () => {
    try {
      const fd = new FormData();
      fd.append('remove', 'true');
      const res = await fetch(`${API}/api/me/photo`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error('Could not remove photo.');
      pushUser({ photo_filename: null });
      setPhotoBust(b => b + 1);
      toast.success('Photo removed.');
    } catch (e) { toast.error(e.message); }
  };

  // ── theme ──
  const chooseTheme = async (t) => {
    setTheme(t);
    try {
      await fetch(`${API}/api/me`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme_preference: t }),
      });
      pushUser({ theme_preference: t });
    } catch {}
  };

  // ── password ──
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwCode, setPwCode] = useState('');
  const [pwCodeSent, setPwCodeSent] = useState(false);
  const sendPwCode = async () => {
    try {
      const res = await fetch(`${API}/api/me/2fa/send-code`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purpose: 'password_change' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || 'Could not send code.');
      setPwCodeSent(true);
      toast.success(`Code sent to ${d.sentTo}.`);
    } catch (e) { toast.error(e.message); }
  };
  const changePassword = async () => {
    if (pw.next !== pw.confirm) { toast.error('New passwords do not match.'); return; }
    try {
      const res = await fetch(`${API}/api/me/password`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: pw.current, newPassword: pw.next, code: pwCode }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not change password.');
      setPw({ current: '', next: '', confirm: '' }); setPwCode(''); setPwCodeSent(false);
      toast.success('Password updated.');
    } catch (e) { toast.error(e.message); }
  };

  // ── 2FA (email codes) ──
  const [twofa, setTwofa] = useState({ enabled: user.twofa_enabled });
  useEffect(() => { setTwofa({ enabled: me.twofa_enabled }); }, [me.twofa_enabled]);
  const [enrollStep, setEnrollStep] = useState(false);
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollSentTo, setEnrollSentTo] = useState('');
  const [sending, setSending] = useState(false);
  const [disablePw, setDisablePw] = useState('');

  const startEnable = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/api/me/2fa/send-code`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purpose: 'enable' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || 'Could not send code.');
      setEnrollSentTo(d.sentTo); setEnrollStep(true); setEnrollCode('');
      toast.success(`We emailed a code to ${d.sentTo}.`);
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };
  const confirmEnable = async () => {
    try {
      const res = await fetch(`${API}/api/me/2fa/enable`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: enrollCode }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Invalid code.');
      setEnrollStep(false); setEnrollCode('');
      setTwofa({ enabled: true }); pushUser({ twofa_enabled: true });
      toast.success('Two-step verification is on.');
    } catch (e) { toast.error(e.message); }
  };
  const disable2fa = async () => {
    try {
      const res = await fetch(`${API}/api/me/2fa/disable`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: disablePw }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not disable.');
      setDisablePw(''); setTwofa({ enabled: false }); pushUser({ twofa_enabled: false });
      toast.success('Two-step verification disabled.');
    } catch (e) { toast.error(e.message); }
  };

  const inputCls = `w-full p-2.5 rounded focus:outline-none focus:ring-2 ${T.input}`;
  const btn = 'px-4 py-2 rounded font-semibold text-white text-sm disabled:opacity-50';

  return (
    <div className={`min-h-screen ${T.page}`}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={onBack} className={`flex items-center gap-1.5 text-sm mb-6 ${T.sub} hover:opacity-80`}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <h1 className={`text-3xl font-bold mb-6 ${T.title}`}>My Account</h1>

        <div className="space-y-5">
          {/* Photo + identity */}
          <Section T={T} title="Profile">
            <div className="flex items-center gap-5">
              <Avatar userId={me.id} bust={photoBust} dark={dark} />
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ''; }} />
                <button onClick={() => fileRef.current?.click()} className={btn} style={{ backgroundColor: BRAND }}>
                  <Camera size={15} className="inline mr-1.5 -mt-0.5" />Change photo
                </button>
                {me.photo_filename && (
                  <button onClick={removePhoto} className={`text-sm flex items-center gap-1 ${T.sub} hover:text-red-400`}>
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <div>
                <label className={`block text-sm mb-1 ${T.sub}`}>Display name</label>
                <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${T.sub}`}>Pronouns <span className="opacity-60">(optional)</span></label>
                <input className={inputCls} value={pronouns} onChange={(e) => setPronouns(e.target.value)} placeholder="e.g. she/her, they/them" />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${T.sub}`}>Email</label>
                <input className={`${inputCls} opacity-60 cursor-not-allowed`} value={me.email || ''} disabled />
                <p className={`text-xs mt-1 ${T.sub}`}>Contact an admin to change your email.</p>
              </div>
              <button onClick={saveProfile} className={btn} style={{ backgroundColor: BRAND }}>Save profile</button>
            </div>
          </Section>

          {/* Appearance */}
          <Section T={T} title="Appearance">
            <div className="flex gap-3">
              {['dark', 'light'].map(opt => (
                <button key={opt} onClick={() => chooseTheme(opt)}
                  className={`flex-1 py-3 rounded-lg border-2 capitalize font-medium transition ${
                    theme === opt ? 'text-white' : `${T.sub} ${T.divider}`}`}
                  style={theme === opt ? { backgroundColor: BRAND, borderColor: BRAND } : {}}>
                  {opt} mode
                </button>
              ))}
            </div>
          </Section>

          {/* Install app — mobile only. On desktop people just use the browser
              tab, and the desktop install path is finicky, so we don't surface it. */}
          {pwa.isMobile && (
            <Section T={T} title="Install App"
              desc="Add OlogyHQ to your home screen to launch it like a native app.">
              {pwa.installed ? (
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle2 size={18} /> <span className="font-medium">OlogyHQ is installed on this device.</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Android hands us a one-click prompt; iOS never does. */}
                  {pwa.canInstall && (
                    <button onClick={installApp} className={btn} style={{ backgroundColor: BRAND }}>
                      <Download size={15} className="inline mr-1.5 -mt-0.5" />Install OlogyHQ
                    </button>
                  )}
                  <div className={`rounded-lg p-3.5 text-sm ${dark ? 'bg-gray-700/40' : 'bg-gray-100'} ${T.sub}`}>
                    {pwa.isIOS ? (
                      <div className="space-y-2.5">
                        <p className={`font-medium ${T.text}`}>Install on your iPhone or iPad</p>
                        <ol className="space-y-2.5">
                          {[
                            <>In your <span className="font-semibold">Safari</span> browser, tap the <span className="font-semibold">•••</span> (More) button in the toolbar.</>,
                            <>Tap <span className="font-semibold">Share</span> <Share size={13} className="inline -mt-0.5" />.</>,
                            <>Choose <span className="font-semibold">Add to Home Screen</span>, then tap <span className="font-semibold">Add</span>.</>,
                          ].map((step, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                              <span
                                className="flex-shrink-0 w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center mt-px"
                                style={{ backgroundColor: BRAND }}
                              >
                                {i + 1}
                              </span>
                              <span className="leading-5">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : (
                      <p className="flex items-start gap-2">
                        <Download size={15} className="mt-0.5 shrink-0" />
                        <span>
                          <span className="font-medium">Android (Chrome):</span> open the <span className="font-medium">⋮</span> menu
                          and tap <span className="font-medium">“Add to Home screen”</span> / <span className="font-medium">“Install app.”</span>
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Notifications */}
          <Section T={T} title="Notifications" desc="Get push notifications on this device.">
            {!pushState.supported ? (
              <p className={`text-sm ${T.sub}`}>This browser doesn’t support push notifications.</p>
            ) : (pwa.isIOS && !pwa.installed) ? (
              <p className={`text-sm ${T.sub}`}>
                On iPhone/iPad, push notifications only work once you
                <span className="font-medium"> install OlogyHQ to your Home Screen</span> (see “Install App” above)
                and open it from there.
              </p>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={togglePush}
                  disabled={pushBusy}
                  className={btn}
                  style={{ backgroundColor: pushState.subscribed ? '#52525b' : BRAND }}
                >
                  {pushBusy ? 'Working…' : pushState.subscribed ? 'Turn off notifications' : 'Turn on notifications'}
                </button>
                {pushState.permission === 'denied' && (
                  <p className={`text-xs ${T.sub}`}>
                    Notifications are blocked in your browser/device settings — re-enable them there to turn these on.
                  </p>
                )}
                {pushState.subscribed && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button onClick={() => testPush(0)} className={`px-3 py-2 rounded text-sm ${T.chip} hover:opacity-80`}>
                      Send test now
                    </button>
                    <button onClick={() => testPush(10)} className={`px-3 py-2 rounded text-sm ${T.chip} hover:opacity-80`}>
                      Test in 10s (background the app)
                    </button>
                  </div>
                )}
                <button onClick={() => onNavigate('notifications')} className="text-sm font-medium hover:underline pt-1" style={{ color: BRAND }}>
                  Choose what you’re notified about →
                </button>
              </div>
            )}
          </Section>

          {/* My Access */}
          <Section T={T} title="My Access" desc="Your roles and the tools you can currently open.">
            <div className="mb-3">
              <div className={`text-sm mb-1 ${T.sub}`}>Roles</div>
              <div className="flex flex-wrap gap-1.5">
                {(me.roles || []).map(r => (
                  <span key={r} className={`px-2 py-0.5 rounded text-xs ${T.chip}`}>{r}</span>
                ))}
              </div>
            </div>
            <div className={`text-sm mb-1 ${T.sub}`}>Tools ({tools.length}) <span className="opacity-60">— tap to open</span></div>
            <div className="flex flex-wrap gap-1.5">
              {tools.map(t => {
                const target = toolNavTarget(t);
                const clickable = !!target && !!onNavigate;
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={!clickable}
                    onClick={clickable ? () => {
                      if (target.url) window.open(target.url, '_blank', 'noopener,noreferrer');
                      else onNavigate(target.page, { canUpload: t.has_upload_permission });
                    } : undefined}
                    className={`px-2 py-0.5 rounded text-xs ${T.chip} ${clickable ? 'hover:opacity-80 cursor-pointer' : 'opacity-60 cursor-default'}`}
                    title={clickable ? `Open ${t.name}` : undefined}
                  >
                    {t.name}{t.has_upload_permission ? ' ✎' : ''}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Password */}
          <Section T={T} title="Change Password">
            <div className="space-y-3">
              <input type="password" className={inputCls} placeholder="Current password"
                value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
              <input type="password" className={inputCls} placeholder="New password (min 6 chars)"
                value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
              <input type="password" className={inputCls} placeholder="Confirm new password"
                value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
              {me.twofa_enabled && (
                <div>
                  <p className={`text-xs mb-2 ${T.sub}`}>Two-step verification is on, so this change needs an email code.</p>
                  <div className="flex gap-2">
                    <input className={`${inputCls} font-mono tracking-widest`} inputMode="numeric" maxLength={6}
                      placeholder="Email code" value={pwCode} onChange={(e) => setPwCode(e.target.value.replace(/\D/g, ''))} />
                    <button type="button" onClick={sendPwCode}
                      className={`px-3 py-2 rounded text-sm whitespace-nowrap ${T.chip} hover:opacity-80`}>
                      {pwCodeSent ? 'Resend code' : 'Email me a code'}
                    </button>
                  </div>
                </div>
              )}
              <button onClick={changePassword}
                disabled={!pw.current || !pw.next || (me.twofa_enabled && pwCode.length < 6)}
                className={btn} style={{ backgroundColor: BRAND }}>
                Update password
              </button>
            </div>
          </Section>

          {/* 2FA */}
          <Section T={T} title="Two-Step Verification"
            desc="Get a one-time code by email when you sign in and when you change your password.">
            {twofa.enabled ? (
              <div>
                <div className="flex items-center gap-2 text-green-400 mb-4">
                  <ShieldCheck size={18} /> <span className="font-medium">On — we email a code at login and to change your password.</span>
                </div>
                <label className={`block text-sm mb-1 ${T.sub}`}>Enter your password to turn it off</label>
                <div className="flex gap-2">
                  <input type="password" className={inputCls} value={disablePw} onChange={(e) => setDisablePw(e.target.value)} />
                  <button onClick={disable2fa} disabled={!disablePw}
                    className="px-4 py-2 rounded font-semibold text-white text-sm bg-red-600 disabled:opacity-50 whitespace-nowrap">
                    Disable
                  </button>
                </div>
              </div>
            ) : enrollStep ? (
              <div>
                <p className={`text-sm mb-3 ${T.sub}`}>
                  We emailed a 6-digit code to <span className="font-medium">{enrollSentTo}</span>. Enter it to turn on two-step verification.
                </p>
                <div className="flex gap-2">
                  <input className={`${inputCls} font-mono tracking-widest`} inputMode="numeric" maxLength={6}
                    placeholder="000000" value={enrollCode} onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ''))} />
                  <button onClick={confirmEnable} disabled={enrollCode.length < 6} className={btn} style={{ backgroundColor: BRAND }}>
                    Verify
                  </button>
                </div>
                <div className="flex gap-4 mt-3">
                  <button onClick={startEnable} disabled={sending} className={`text-sm ${T.sub} hover:opacity-80`}>Resend code</button>
                  <button onClick={() => { setEnrollStep(false); setEnrollCode(''); }} className={`text-sm ${T.sub} hover:opacity-80`}>Cancel</button>
                </div>
              </div>
            ) : (
              <div>
                <div className={`flex items-center gap-2 mb-4 ${T.sub}`}>
                  <Shield size={18} /> <span>Not enabled.</span>
                </div>
                <button onClick={startEnable} disabled={sending} className={btn} style={{ backgroundColor: BRAND }}>
                  {sending ? 'Sending…' : 'Turn on two-step verification'}
                </button>
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
